import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverBody,
  Flex,
  Select,
  Box,
  Button,
  HStack,
  VStack,
  Text,
} from "@chakra-ui/react";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export function OfiDateTimeRangePicker({
  start,
  end,
  setStart,
  setEnd,
  earliestPossibleDate,
  lastPossibleDate,
}: {
  start: Date | null;
  setStart: (date: Date | null) => void;
  end: Date | null;
  setEnd: (date: Date | null) => void;
  earliestPossibleDate?: Date | null;
  lastPossibleDate?: Date | null;
}) {
  return (
    <VStack align={"left"}>
      <Text size={"xs"}>Starttid</Text>
      <OfiDateTimePicker
        startDate={start}
        endDate={end}
        selectsStart={true}
        selected={start}
        onChange={setStart}
        earliestPossibleDate={earliestPossibleDate ?? undefined}
        latestPossibleDate={lastPossibleDate ?? undefined}
      />
      <Text size={"xs"}>Sluttid</Text>
      <OfiDateTimePicker
        startDate={start}
        endDate={end}
        selectsEnd={true}
        selected={end}
        onChange={setEnd}
        earliestPossibleDate={earliestPossibleDate ?? undefined}
        latestPossibleDate={lastPossibleDate ?? undefined}
      />
    </VStack>
  );
}
export function OfiDateTimePicker({
  selected,
  startDate,
  endDate,
  onChange,
  selectsStart,
  selectsEnd,
  borderWidth,
  borderColor,
  borderStyle,
  earliestPossibleDate,
  latestPossibleDate,
}: {
  selected: Date | null | undefined;
  startDate: Date | null | undefined;
  endDate?: Date | null;
  onChange: (date: Date | null) => void;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  borderWidth?: string;
  borderColor?: string;
  borderStyle?: string;
  earliestPossibleDate?: Date;
  latestPossibleDate?: Date;
}) {
  const [date, setDate] = useState<Date | null>(
    selected ?? earliestPossibleDate ?? null
  );
  const [time, setTime] = useState<Date>(
    selected ?? earliestPossibleDate ?? new Date()
  );
  const [earliestDate, setEarliestDate] = useState<Date | undefined>(
    (selectsEnd && startDate) || earliestPossibleDate
  );
  const [latestDate, setLatestDate] = useState<Date | undefined>(
    latestPossibleDate || new Date(31536000000 * 500)
  ); // Approx 500 years after Jan 1970

  useEffect(() => {
    setEarliestDate((selectsEnd && startDate) || earliestPossibleDate);
    setLatestDate(latestPossibleDate);
  }, [earliestPossibleDate, latestPossibleDate]);

  useEffect(() => {
    if (date) {
      const newDate = projectDateToTimeLimits(date, earliestDate, latestDate);
      setTime(newDate);
    } else {
      onChange(null);
    }
  }, [date]);

  useEffect(() => {
    if (date) {
      const d = new Date(date);
      d.setHours(time.getHours());
      d.setMinutes(time.getMinutes());
      onChange(d);
    } else {
      onChange(null);
    }
  }, [time]);

  return (
    <HStack
      marginBottom={"10px"}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderStyle={borderStyle}
    >
      <Box
        background={"white"}
        borderWidth={"2px"}
        padding={"5px"}
        borderRadius={"10px"}
        width={"fit-content"}
      >
        <DatePicker
          calendarStartDay={1}
          showIcon={endDate == null}
          icon={<CalendarIcon />}
          selected={date}
          onChange={setDate}
          selectsStart={selectsStart}
          selectsEnd={selectsEnd}
          startDate={startDate}
          endDate={endDate}
          minDate={earliestDate}
          maxDate={latestDate}
          placeholderText={
            selectsStart
              ? "  Välj startdatum"
              : selectsEnd
              ? "  Välj slutdatum"
              : "  Välj datum"
          }
        />
      </Box>
      {date != null && (
        <OfiTimePicker
          initialTime={time}
          onChange={setTime}
          earliestTime={earliestDate}
          latestTime={latestDate}
        />
      )}
    </HStack>
  );
}

export function OfiTimePicker(props: {
  initialTime: Date;
  onChange: (date: Date) => void;
  earliestTime?: Date;
  latestTime?: Date;
}) {
  const [date, setDate] = useState<Date>(props.initialTime);

  const selectedHour = (date: Date) => {
    return date.getHours().toString().padStart(2, "0");
  };

  const selectedMinutes = (date: Date) => {
    return date.getMinutes().toString().padStart(2, "0");
  };

  const [possibleHours, setPossibleHours] = useState<Array<number>>([]);
  const [possibleMinutes, setPossibleMinutes] = useState<Array<number>>([]);

  useEffect(() => {
    const timeLimits = projectTimeLimitsOnDate(
      props.earliestTime,
      props.latestTime,
      props.initialTime
    );

    if (timeLimits == null) {
      setPossibleHours([]);
      setPossibleMinutes([]);
      console.error("Time selector incorrectly used");
    } else {
      const earliestHour = timeLimits.beginning.getHours();
      const latestHour = timeLimits.end.getHours();

      const earliestMinute =
        earliestHour < props.initialTime.getHours()
          ? 0
          : timeLimits.beginning.getMinutes();

      const latestMinute =
        latestHour > props.initialTime.getHours()
          ? 59
          : timeLimits.end.getMinutes();

      setPossibleHours(
        [...Array(24).keys()].filter(
          (h) => h >= earliestHour && h <= latestHour
        )
      );

      setPossibleMinutes(
        [...Array(60).keys()].filter(
          (m) => m >= earliestMinute && m <= latestMinute
        )
      );

      // Auto select to fit constraint times
      if (props.initialTime.getTime() < timeLimits.beginning.getTime()) {
        setDate(timeLimits.beginning);
      } else if (props.initialTime.getTime() > timeLimits.end.getTime()) {
        setDate(timeLimits.end);
      }
    }
  }, [
    props.earliestTime,
    props.latestTime,
    setPossibleHours,
    setPossibleMinutes,
  ]);

  useEffect(() => {
    const timeLimits = projectTimeLimitsOnDate(
      props.earliestTime,
      props.latestTime,
      props.initialTime
    );

    if (timeLimits && date.getTime() < timeLimits.beginning.getTime()) {
      setDate(timeLimits.beginning);
    } else if (timeLimits && date.getTime() > timeLimits.end.getTime()) {
      setDate(timeLimits.end);
    }

    props.onChange(date);
  }, [date, selectedHour(date), selectedMinutes(date)]);

  return (
    <Box>
      <Popover>
        <PopoverTrigger>
          <Button
            variant={"outline"}
            borderWidth={"2px"}
            fontWeight={"normal"}
            background={"white"}
          >
            {`Tid: ${selectedHour(date)}:${selectedMinutes(date)}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          background={"blue.50"}
          shadow={"2xl"}
          width={"fit-content"}
        >
          <PopoverArrow backgroundColor={"blue.50"} />
          <PopoverHeader borderColor={"gray.500"} background={"green.100"}>
            Välj tid
          </PopoverHeader>
          <PopoverBody background={"white"}>
            <Flex>
              <Select
                width={"4xs"}
                background={"white"}
                placeholder="Timmar"
                value={selectedHour(date)}
                onChange={(e) => {
                  if (e.target.value.match(/^\d+$/)) {
                    const newDate = new Date(props.initialTime);
                    newDate.setHours(parseInt(e.target.value));
                    setDate(newDate);
                  }
                }}
              >
                {possibleHours.map((hour) => (
                  <option key={hour} value={hour.toString().padStart(2, "0")}>
                    {hour.toString().padStart(2, "0")}
                  </option>
                ))}
              </Select>
              <Select
                width={"4xs"}
                background={"white"}
                placeholder="Minuter"
                value={selectedMinutes(date)}
                onChange={(e) => {
                  if (e.target.value.match(/^\d+$/)) {
                    const newDate = new Date(props.initialTime);
                    newDate.setMinutes(parseInt(e.target.value));
                    setDate(newDate);
                  }
                }}
              >
                {possibleMinutes.map((minute) => (
                  <option
                    key={minute}
                    value={minute.toString().padStart(2, "0")}
                  >
                    {minute.toString().padStart(2, "0")}
                  </option>
                ))}
              </Select>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
}

// Returns -1 if a is an earlier day than b
// Returns 0 if a is the same day as b
// Returns 1 if a is a later day than b
function compareDay(a: Date, b: Date) {
  const yearDiff = toComparable(a.getFullYear() - b.getFullYear());
  const monthDiff = toComparable(a.getMonth() - b.getMonth());
  const dayDiff = toComparable(a.getDate() - b.getDate());
  return yearDiff || monthDiff || dayDiff;
}

function toComparable(a: number) {
  return a < 0 ? -1 : a === 0 ? 0 : 1;
}

function projectTimeLimitsOnDate(
  earliestPossibe: Date | undefined,
  latestPossible: Date | undefined,
  actualDay: Date
) {
  const selectedDayTooEarly = earliestPossibe
    ? compareDay(actualDay, earliestPossibe) === -1
    : false;
  const selectedDayTooLate = latestPossible
    ? compareDay(actualDay, latestPossible) === 1
    : false;

  if (
    selectedDayTooEarly ||
    selectedDayTooLate ||
    (earliestPossibe &&
      latestPossible &&
      earliestPossibe.getTime() > latestPossible.getTime())
  ) {
    return null;
  } else {
    const dayMatchesEarliest = earliestPossibe
      ? compareDay(actualDay, earliestPossibe) === 0
      : false;
    const dayMatchesLatest = latestPossible
      ? compareDay(actualDay, latestPossible) === 0
      : false;

    let beginning = earliestPossibe;
    if (!dayMatchesEarliest || earliestPossibe == null) {
      beginning = new Date(actualDay);
      beginning.setHours(0, 0, 0, 0);
    }

    let end = latestPossible;
    if (!dayMatchesLatest || latestPossible == null) {
      end = new Date(actualDay);
      end.setHours(23, 59, 59, 999);
    }

    return { beginning: beginning as Date, end: end as Date };
  }
}

function projectDateToTimeLimits(
  date: Date,
  earliestPossibe?: Date,
  latestPossible?: Date
) {
  const selectedDayTooEarly = earliestPossibe
    ? compareDay(date, earliestPossibe) === -1
    : false;
  const selectedDayTooLate = latestPossible
    ? compareDay(date, latestPossible) === 1
    : false;

  if (selectedDayTooEarly) {
    return new Date(earliestPossibe!);
  } else if (selectedDayTooLate) {
    return new Date(latestPossible!);
  } else {
    return new Date(date);
  }
}
